import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  db, doc, getDoc, setDoc, updateDoc, addDoc, collection,
  serverTimestamp,
} from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { notifyCheckoutGifted } from '../utils/notifications';
import {
  isTokenValid,
  msUntilExpiry,
  formatTimeLeft,
} from '../utils/shareTokens';
import {
  DEFAULT_SHIPPING,
  DELIVERY_PROVIDERS,
  getProviderRate,
  getProProviders,
  requiresAddress,
} from '../utils/deliveryProviders';
import {
  ShoppingBag, User, CreditCard, AlertCircle, Check, Loader2,
  Lock, Sparkles, Calendar, Clock, Wallet, Banknote, Smartphone,
  Package, MapPin,
} from 'lucide-react';

const PAYSTACK_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '';

// ============================================================
// HELPERS
// ============================================================
const cleanForFirestore = (obj) => {
  if (Array.isArray(obj)) return obj.map(cleanForFirestore);
  if (obj && typeof obj === 'object' && obj.constructor === Object) {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined) out[k] = cleanForFirestore(v);
    });
    return out;
  }
  return obj;
};

const formatPrice = (p) => `R${Number(p || 0).toFixed(2)}`;

const formatBookingDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-ZA', {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  } catch {
    return iso;
  }
};

// ============================================================
// PAGE
// ============================================================
const SharedCheckoutPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, userData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);
  const [vendors, setVendors] = useState({});

  // Guest payer details
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);

  // Prefill from logged-in user
  useEffect(() => {
    if (userData?.name && !payerName) setPayerName(userData.name);
    if ((userData?.email || user?.email) && !payerEmail) {
      setPayerEmail(userData?.email || user?.email);
    }
  }, [userData, user]);

  // ---------- Load share doc ----------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!token) {
          setError('Missing share token.');
          return;
        }

        const snap = await getDoc(doc(db, 'shareLinks', token));
        if (cancelled) return;

        if (!snap.exists()) {
          setError('This share link is invalid or has been removed.');
          return;
        }

        const data = snap.data();

        if (data.type !== 'checkout') {
          setError('This link is not a checkout share.');
          return;
        }
        if (!isTokenValid(data)) {
          setError('This share link has expired.');
          return;
        }
        if (data.paid) {
          setError('This checkout has already been paid. Ask the sender for a new link.');
          return;
        }

        setShare(data);

        // Load vendor shipping configs for delivery fee recompute
        const vendorIds = Object.keys(data.vendorsSnapshot || {});
        const proDocs = {};
        for (const proId of vendorIds) {
          if (proId === '__global__') continue;
          try {
            const proSnap = await getDoc(doc(db, 'users', proId));
            proDocs[proId] = proSnap.exists() ? proSnap.data() : null;
          } catch {
            proDocs[proId] = null;
          }
        }
        if (!cancelled) setVendors(proDocs);
      } catch (err) {
        console.error('Failed to load shared checkout:', err);
        if (!cancelled) setError('Something went wrong loading this checkout.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  // ============================================================
  // DERIVED VALUES
  // ============================================================
  const items = share?.items || [];
  const bookings = share?.bookings || [];
  const deliveryMethods = share?.deliveryMethods || {};
  const deliveryAddress = share?.deliveryAddress || null;
  const vendorsSnapshot = share?.vendorsSnapshot || {};

  // Group items by vendor
  const vendorsByItems = (() => {
    const map = new Map();
    items.forEach(item => {
      const proId = item.professionalId || '__global__';
      if (!map.has(proId)) {
        const snap = vendorsSnapshot[proId] || {};
        map.set(proId, {
          proId,
          proName: snap.proName || item.professionalName || 'Skytone',
          proImage: snap.proImage || null,
          items: [],
        });
      }
      map.get(proId).items.push(item);
    });
    return Array.from(map.values());
  })();

  const getVendorSubtotal = (v) =>
    v.items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0);

  const getVendorDeliveryFee = (vendor, providerId) => {
    const snap = vendorsSnapshot[vendor.proId] || {};
    const shipping = { ...DEFAULT_SHIPPING, ...(snap.shipping || {}) };
    const subtotal = getVendorSubtotal(vendor);
    const threshold = shipping.freeShippingThreshold || 0;
    const provider = DELIVERY_PROVIDERS[providerId];
    if (provider?.type === 'door' && threshold > 0 && subtotal >= threshold) return 0;
    return getProviderRate(providerId, shipping);
  };

  // Recompute amounts from the snapshot (safer than trusting stored totals)
  const subtotal = items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0);

  const shippingTotal = vendorsByItems.reduce((s, v) => {
    const method = deliveryMethods[v.proId];
    if (!method) return s;
    return s + getVendorDeliveryFee(v, method);
  }, 0);

  const productsTotalNow = subtotal + shippingTotal;

  const getBookingTotal = (b) =>
    Number(b.totalPrice ??
      ((b.price || 0) + Number(b.addonsTotal || 0) + (b.transportFee || 0)));

  const getBookingAmountForMethod = (b, methodId) => {
    const total = getBookingTotal(b);
    const policy = b.paymentPolicy || 'after';
    if (methodId === 'cash') return 0;
    if (policy === 'after') return 0;
    if (policy === 'full') return total;
    if (policy === 'deposit') {
      return Number(b.depositAmount || Math.round(total * ((b.depositPercent || 30) / 100)));
    }
    return 0;
  };

  const bookingGroups = (() => {
    const card = [];
    const payshap = [];
    const cash = [];
    bookings.forEach(b => {
      const method = b.chosenMethod || 'card';
      const entry = { booking: b, method };
      if (method === 'payshap') payshap.push(entry);
      else if (method === 'cash') cash.push(entry);
      else card.push(entry);
    });
    return { card, payshap, cash };
  })();

  const cardBookingsTotal = bookingGroups.card.reduce(
    (s, { booking, method }) => s + getBookingAmountForMethod(booking, method), 0
  );

  const paystackTotal = productsTotalNow + cardBookingsTotal;

  const payshapTotal = bookingGroups.payshap.reduce(
    (s, { booking, method }) => s + getBookingAmountForMethod(booking, method), 0
  );

  const bookingsGrandTotal = bookings.reduce(
    (s, b) => s + getBookingTotal(b), 0
  );

  // ============================================================
  // CREATE ORDERS + BOOKINGS ON BEHALF OF OWNER
  // ============================================================
  const createProductOrders = async (paymentReference) => {
    if (items.length === 0) return [];

    const payerInfo = {
      userId: user?.uid || null,
      name: payerName.trim(),
      email: payerEmail.trim(),
      isGuest: !user,
    };

    const orderPromises = vendorsByItems.map(vendor => {
      const providerId = deliveryMethods[vendor.proId];
      const provider = DELIVERY_PROVIDERS[providerId];
      const vendorSubtotal = getVendorSubtotal(vendor);
      const deliveryFee = getVendorDeliveryFee(vendor, providerId);
      const needsAddr = requiresAddress(providerId);
      const snap = vendorsSnapshot[vendor.proId] || {};

      const orderData = cleanForFirestore({
        // Owner is the actual buyer of record
        clientId: share.ownerUid,
        clientName: deliveryAddress?.fullName || share.ownerName || 'Skytone user',
        clientEmail: share.ownerEmail || '',
        clientPhone: deliveryAddress?.phone || '',

        vendorId: vendor.proId,
        vendorName: vendor.proName,
        vendorImage: vendor.proImage || null,

        items: vendor.items.map(it => ({
          id: it.id,
          name: it.name,
          brand: it.brand || '',
          price: it.price,
          quantity: it.quantity,
          image: it.image || null,
          professionalId: it.professionalId || null,
          professionalName: it.professionalName || null,
        })),

        subtotal: vendorSubtotal,
        deliveryFee,
        total: vendorSubtotal + deliveryFee,
        currency: 'ZAR',

        deliveryMethod: providerId,
        deliveryProvider: provider?.name || 'Delivery',
        deliveryEstimate: provider?.estimate || '',
        deliveryType: provider?.type || 'door',
        deliveryAddress: needsAddr ? { ...(deliveryAddress || {}) } : null,
        collectAddress: !needsAddr
          ? (snap.collectAddress || 'Contact vendor')
          : null,

        // Paid by guest
        paidBy: payerInfo,
        giftedTo: share.ownerUid,

        status: 'paid',
        fulfillmentStatus: 'pending',
        paymentMethod: 'paystack',
        paymentReference,
        source: 'shared_checkout',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return addDoc(collection(db, 'orders'), orderData);
    });

    const refs = await Promise.all(orderPromises);
    return refs.map(r => r.id);
  };

  const createBookingDocs = async (paymentReference) => {
    if (bookings.length === 0) return;

    const payerInfo = {
      userId: user?.uid || null,
      name: payerName.trim(),
      email: payerEmail.trim(),
      isGuest: !user,
    };

    const stripPendingFields = (b) => {
      const { id: _id, addedAt: _a, chosenMethod: _m, ...payload } = b;
      return payload;
    };

    for (const { booking: b, method } of bookingGroups.card) {
      const policy = b.paymentPolicy || 'after';
      let paymentStatus = 'unpaid';
      let amountPaid = 0;
      let amountDue = getBookingTotal(b);

      if (policy === 'full') {
        paymentStatus = 'paid_in_full';
        amountPaid = getBookingTotal(b);
        amountDue = 0;
      } else if (policy === 'deposit') {
        paymentStatus = 'deposit_paid';
        amountPaid = getBookingAmountForMethod(b, 'card');
        amountDue = getBookingTotal(b) - amountPaid;
      }

      await addDoc(collection(db, 'bookings'), cleanForFirestore({
        ...stripPendingFields(b),
        clientId: share.ownerUid,
        status: 'pending',
        paymentStatus,
        amountPaid,
        amountDue,
        paymentMethod: 'card',
        paymentReference,
        paidAt: paymentStatus !== 'unpaid' ? serverTimestamp() : null,
        paidBy: payerInfo,
        giftedTo: share.ownerUid,
        source: 'shared_checkout',
        createdAt: serverTimestamp(),
      }));
    }

    for (const { booking: b } of bookingGroups.payshap) {
      await addDoc(collection(db, 'bookings'), cleanForFirestore({
        ...stripPendingFields(b),
        clientId: share.ownerUid,
        status: 'pending',
        paymentStatus: 'pending_transfer',
        amountPaid: 0,
        amountDue: getBookingTotal(b),
        paymentMethod: 'payshap',
        paymentReference: null,
        paidBy: payerInfo,
        giftedTo: share.ownerUid,
        source: 'shared_checkout',
        createdAt: serverTimestamp(),
      }));
    }

    for (const { booking: b } of bookingGroups.cash) {
      await addDoc(collection(db, 'bookings'), cleanForFirestore({
        ...stripPendingFields(b),
        clientId: share.ownerUid,
        status: 'pending',
        paymentStatus: 'unpaid',
        amountPaid: 0,
        amountDue: getBookingTotal(b),
        paymentMethod: 'cash',
        paymentReference: null,
        paidBy: payerInfo,
        giftedTo: share.ownerUid,
        source: 'shared_checkout',
        createdAt: serverTimestamp(),
      }));
    }
  };

  // ============================================================
  // PLACE ORDER (GUEST PAYS)
  // ============================================================
  const handlePlaceOrder = async () => {
    if (!share) return;

    if (!payerName.trim()) {
      alert('Please enter your name.');
      return;
    }
    if (!payerEmail.trim() || !payerEmail.includes('@')) {
      alert('Please enter a valid email for your receipt.');
      return;
    }

    // Nothing to charge → just create bookings and mark paid
    if (paystackTotal === 0) {
      setPaying(true);
      try {
        await createBookingDocs(null);
        await finalizeShare(null);
        setPaid(true);
      } catch (err) {
        console.error('Checkout (no-payment) failed:', err);
        alert('Could not place the order. Please try again.');
      } finally {
        setPaying(false);
      }
      return;
    }

    if (!PAYSTACK_KEY) {
      alert('Payment is not configured.');
      return;
    }

    setPaying(true);

    const amountInKobo = Math.round(paystackTotal * 100);
    const reference = `skytone_gift_${Date.now()}_${share.ownerUid.slice(0, 6)}`;

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_KEY,
      email: payerEmail.trim(),
      amount: amountInKobo,
      currency: 'ZAR',
      ref: reference,
      metadata: {
        custom_fields: [
          { display_name: 'Payer', variable_name: 'payer_name', value: payerName.trim() },
          { display_name: 'Gifted to', variable_name: 'gifted_to', value: share.ownerName || 'Owner' },
          { display_name: 'Source', variable_name: 'source', value: 'shared_checkout' },
        ],
      },
      callback: async (response) => {
        try {
          await createProductOrders(response.reference || reference);
          await createBookingDocs(response.reference || reference);
          await finalizeShare(response.reference || reference);

          // Notify owner (best effort)
          try {
            await notifyCheckoutGifted({
              ownerId: share.ownerUid,
              payerName: payerName.trim(),
              total: paystackTotal,
              itemCount: items.length,
              bookingCount: bookings.length,
            });
          } catch (e) {
            console.warn('Notify owner failed:', e);
          }

          setPaid(true);
        } catch (err) {
          console.error('Finalize failed:', err);
          alert(
            'Payment succeeded but we could not save the order. Contact support with reference: ' +
            (response.reference || reference)
          );
        } finally {
          setPaying(false);
        }
      },
      onClose: () => {
        setPaying(false);
      },
    });

    handler.openIframe();
  };

  const finalizeShare = async (paymentReference) => {
    try {
      await updateDoc(doc(db, 'shareLinks', token), cleanForFirestore({
        paid: true,
        paidBy: {
          userId: user?.uid || null,
          name: payerName.trim(),
          email: payerEmail.trim(),
          isGuest: !user,
        },
        paidAt: serverTimestamp(),
        paymentReference: paymentReference || null,
        appliedToOwner: false, // mirror will flip this on owner's next HomePage open
      }));
    } catch (err) {
      console.warn('Could not mark share as paid:', err);
    }
  };

  // ============================================================
  // RENDER — STATES
  // ============================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#EFA9B6]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center border border-gray-100">
          <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={36} className="text-[#EFA9B6]" />
          </div>
          <h1 className="text-xl font-serif font-bold text-gray-900 mb-2">
            Link unavailable
          </h1>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            Go to Skytone
          </button>
        </div>
      </div>
    );
  }

  if (paid) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center border border-gray-100">
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={36} className="text-green-500" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">
            You're amazing 🎁
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            {(share.ownerName || 'They').split(' ')[0]} has been notified. Their order is on the way.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            Explore Skytone
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER — MAIN
  // ============================================================
  const timeLeft = formatTimeLeft(msUntilExpiry(share));
  const firstName = (share.ownerName || 'A Skytone user').split(' ')[0];

  return (
    <div className="min-h-screen bg-[#FCF8F9] py-8 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Brand */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[#EFA9B6] font-bold tracking-widest text-xs uppercase">
            <Sparkles size={14} />
            Skytone
          </div>
        </div>

        {/* Owner header */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#FFF5F6] overflow-hidden flex items-center justify-center shrink-0">
              {share.ownerImage ? (
                <img src={share.ownerImage} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={24} className="text-[#EFA9B6]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-0.5">
                Checkout for
              </p>
              <h1 className="text-xl font-serif font-bold text-gray-900 truncate">
                {firstName}'s order
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {items.length} item{items.length === 1 ? '' : 's'}
                {bookings.length > 0 && ` · ${bookings.length} booking${bookings.length === 1 ? '' : 's'}`}
                {' · '}{timeLeft}
              </p>
            </div>
          </div>
        </div>

        {/* Products */}
        {vendorsByItems.length > 0 && (
          <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
            <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">
              Products
            </h2>
            <div className="space-y-4">
              {vendorsByItems.map(vendor => (
                <div key={vendor.proId}>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                    {vendor.proName}
                  </p>
                  {vendor.items.map((it, idx) => (
                    <div key={idx} className="flex gap-3 mb-3 last:mb-0">
                      <div className="w-14 h-14 rounded-2xl bg-[#FCF8F9] flex items-center justify-center shrink-0 overflow-hidden">
                        {it.image ? (
                          <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                        ) : (
                          <ShoppingBag size={20} className="text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-bold text-sm text-gray-900 truncate">{it.name}</p>
                            {it.brand && (
                              <p className="text-xs text-gray-400 truncate">{it.brand}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-0.5">Qty {it.quantity}</p>
                          </div>
                          <span className="font-bold text-sm text-gray-900 shrink-0">
                            {formatPrice((it.price || 0) * (it.quantity || 1))}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bookings */}
        {bookings.length > 0 && (
          <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
            <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <Calendar size={12} className="text-[#EFA9B6]" />
              Bookings
            </h2>
            <div className="space-y-4">
              {bookings.map(b => {
                const total = getBookingTotal(b);
                const policy = b.paymentPolicy || 'after';
                const method = b.chosenMethod || 'card';
                const amountNow = getBookingAmountForMethod(b, method);

                return (
                  <div key={b.id} className="pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#FFF5F6] flex items-center justify-center shrink-0 overflow-hidden">
                        {b.professionalImage ? (
                          <img src={b.professionalImage} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User size={16} className="text-[#EFA9B6]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900 truncate">
                          {b.serviceTitle}
                          {b.optionName && <span className="text-[#AF8BD4]"> · {b.optionName}</span>}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{b.professionalName}</p>
                        <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-1">
                          <Calendar size={10} />
                          <span>{formatBookingDate(b.date)}</span>
                          <Clock size={10} />
                          <span>{b.time}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          {method === 'card' && <Wallet size={10} className="text-[#AF8BD4]" />}
                          {method === 'payshap' && <Smartphone size={10} className="text-[#AF8BD4]" />}
                          {method === 'cash' && <Banknote size={10} className="text-[#AF8BD4]" />}
                          <span className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-wider">
                            {method === 'card' && 'Card'}
                            {method === 'payshap' && 'PayShap'}
                            {method === 'cash' && 'Cash at appointment'}
                          </span>
                          {policy === 'full' && <span className="text-[10px] text-gray-400">· Full upfront</span>}
                          {policy === 'deposit' && <span className="text-[10px] text-gray-400">· {b.depositPercent || 30}% deposit</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        {amountNow > 0 ? (
                          <>
                            <p className="font-bold text-sm text-[#EFA9B6]">{formatPrice(amountNow)}</p>
                            <p className="text-[10px] text-gray-400">now</p>
                          </>
                        ) : (
                          <p className="font-bold text-sm text-gray-400">R0 now</p>
                        )}
                        <p className="text-[10px] text-gray-400">{formatPrice(total)} total</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Delivery address — only if needed */}
        {deliveryAddress && (
          <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
            <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <MapPin size={12} className="text-[#EFA9B6]" />
              Delivery Address
            </h2>
            <p className="text-sm font-bold text-gray-900">{deliveryAddress.fullName}</p>
            <p className="text-xs text-gray-600 mt-1">{deliveryAddress.line1}</p>
            <p className="text-xs text-gray-600">
              {deliveryAddress.city}, {deliveryAddress.postalCode}
            </p>
            <p className="text-xs text-gray-500 mt-1">{deliveryAddress.country}</p>
          </div>
        )}

        {/* Totals */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">
            Payment Summary
          </h2>
          <div className="space-y-2 text-sm">
            {productsTotalNow > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Products + delivery (card)</span>
                <span className="font-bold text-gray-900">{formatPrice(productsTotalNow)}</span>
              </div>
            )}
            {cardBookingsTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">
                  {bookingGroups.card.length} booking{bookingGroups.card.length === 1 ? '' : 's'} (card)
                </span>
                <span className="font-bold text-gray-900">{formatPrice(cardBookingsTotal)}</span>
              </div>
            )}
            {payshapTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">
                  {bookingGroups.payshap.length} booking{bookingGroups.payshap.length === 1 ? '' : 's'} (PayShap)
                </span>
                <span className="font-bold text-[#AF8BD4]">
                  {formatPrice(payshapTotal)} — sends separately
                </span>
              </div>
            )}
            {bookingGroups.cash.length > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">
                  {bookingGroups.cash.length} booking{bookingGroups.cash.length === 1 ? '' : 's'} (cash)
                </span>
                <span className="font-bold text-gray-500">Pay at appointment</span>
              </div>
            )}
            <div className="flex justify-between pt-3 border-t border-gray-100">
              <span className="font-bold text-gray-900">
                {paystackTotal > 0 ? 'Pay now via card' : 'Nothing to pay now'}
              </span>
              <span className="font-bold text-[#EFA9B6] text-lg">
                {formatPrice(paystackTotal)}
              </span>
            </div>
          </div>
        </div>

        {/* Payer details */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">
            Your details
          </h2>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Your name"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:border-[#EFA9B6] focus:ring-2 focus:ring-[#EFA9B6]/20 outline-none text-sm"
            />
            <input
              type="email"
              placeholder="Your email (for receipt)"
              value={payerEmail}
              onChange={(e) => setPayerEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:border-[#EFA9B6] focus:ring-2 focus:ring-[#EFA9B6]/20 outline-none text-sm"
            />
            {!user && (
              <p className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <Lock size={11} />
                No account needed — we'll email your receipt.
              </p>
            )}
          </div>
        </div>

        {/* Pay button */}
        <button
          onClick={handlePlaceOrder}
          disabled={paying || !payerName.trim() || !payerEmail.includes('@')}
          className="w-full bg-[#EFA9B6] text-white py-4 rounded-full font-bold text-sm shadow-lg hover:bg-[#EFA9B6]/90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {paying ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Processing…
            </>
          ) : paystackTotal > 0 ? (
            <>
              <CreditCard size={18} />
              Pay {formatPrice(paystackTotal)} for {firstName}
            </>
          ) : (
            <>
              <Check size={18} />
              Place Order for {firstName}
            </>
          )}
        </button>

        <p className="text-center text-[11px] text-gray-400 mt-4">
          Secured by Paystack
        </p>
      </div>
    </div>
  );
};

export default SharedCheckoutPage;